import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import pick from "lodash/pick";

import { isApolloError } from "@apollo/client";
import { ApolloError } from "@apollo/client/errors";

import { Toast } from "@covertree/utils";
import { DEFAULT_NO_EMAIL_BASE } from "@covertree/consts";
import {
  CheckAndAddPriorClaimDocument,
  CheckAndAddPriorClaimMutation,
  CheckAndAddPriorClaimMutationVariables,
  CreateOrUpdateBasicPolicyDetailsWithHolderDocument,
  CreateOrUpdateBasicPolicyDetailsWithHolderMutation,
  CreateOrUpdateBasicPolicyDetailsWithHolderMutationVariables,
  ExtraCoverageKeys,
  extraCoverageKeys,
  GetExtraCoveragesPricesDocument,
  GetExtraCoveragesPricesQuery,
  GetExtraCoveragesPricesQueryVariables,
  GeoCodeInput,
  OrganizationType,
  Policy,
  PolicyholderType,
  PolicyLevelCoverages,
  PolicyLevelUnderwritingAnswers,
  PolicyUsage,
  PriorClaimInput,
  PriorInsurance,
  PriorInsuranceDetailsInput,
  SaveCustomizedPolicyDocument,
  SaveCustomizedPolicyMutation,
  SaveCustomizedPolicyMutationVariables,
  SaveExtraCoveragesDocument,
  SaveExtraCoveragesMutation,
  SaveExtraCoveragesMutationVariables,
  SavePolicyLevelExtraCoveragesInput,
  SavePriorClaimsDocument,
  SavePriorClaimsMutation,
  SavePriorClaimsMutationVariables,
  SaveUnitLevelExtraCoveragesInput,
  SelectQuoteDocument,
  SelectQuoteMutation,
  SelectQuoteMutationVariables,
  UnderwritingPolicyAnswersInput,
  UnderwritingQuestion,
  UnderwritingUnitAnswersInput,
  UnitLevelCoverages,
  UnitLevelUnderwritingAnswers,
  UpdatePhysicallyMailDocumentsDocument,
  UpdatePhysicallyMailDocumentsMutation,
  UpdatePhysicallyMailDocumentsMutationVariables,
  UpdatePolicyholderDetailsDocument,
  UpdatePolicyholderDetailsInput,
  UpdatePolicyholderDetailsMutation,
  UpdatePolicyholderDetailsMutationVariables,
  UpdateUnderwritingAnswersDocument,
  UpdateUnderwritingAnswersMutation,
  UpdateUnderwritingAnswersMutationVariables,
  GetDynamicQuestionsDocument,
  CreateOrUpdatePolicyholderInput,
  SaveInternalTrackingSubcodeMutation,
  SaveInternalTrackingSubcodeMutationVariables,
  SaveInternalTrackingSubcodeDocument,
  ConsumerContinueQuoteMutation,
  ConsumerContinueQuoteMutationVariables,
  ConsumerContinueQuoteDocument,
} from "@covertree/new-business-graphql";
import {
  CanOperateInStateDocument,
  CanOperateInStateResult,
  ContinueQuoteDocument,
  ContinueQuoteMutation,
  ContinueQuoteMutationVariables,
} from "@covertree/agent-portal-graphql";
import {
  ContactDetailsForm,
  mergeUnderwritingAnswers,
  PersonalDetailsForm,
} from "@covertree/new-business-forms";
import { CountyBlockerErrorType, RedZoneErrorType } from "@covertree/types";

import {
  getBasicPolicyDetails,
  setBasicPolicyDetailsBasedOnPolicy,
  setPolicyEffectiveDate,
} from "../basicPolicyDetails";
import {
  getPropertyAddressDetails,
  setHomeAddressBasedOnPolicyDetails,
} from "../homeAddress";
import {
  ExtraQuestions,
  getNonPolicyholderPersonalDetails,
  getPersonalDetails,
  setContactDetailsWithinPersonalDetails,
  setExtraQuestion,
  setPersonalDetails,
  setPersonalDetailsBasedOnPolicy,
  getLeadSource,
} from "../personalDetails";
import {
  setCustomizationOptions,
  setCustomizationUnitsWithCoveragesCoverageBC,
} from "../policyCustomizationOptions";
import {
  getUnitConstructionAndDetails,
  setUnitsConstructionAndDetailsBasedOnPolicy,
} from "../unitsConstructionAndDetails";
import { setDynamicQuestions } from "../dynamicQuestions";

import { createAsyncThunkSlice, typedReducerCreator } from "../../utils";
import { PolicyErrors } from "@covertree/types/PolicyErrors";

export type NewBusinessPolicyState =
  CreateOrUpdateBasicPolicyDetailsWithHolderMutation["createOrUpdateBasicPolicyDetailsWithHolder"] &
    Pick<Policy, "priorClaims" | "startDateAfterUWReview"> &
    SavePriorClaimsMutation["savePriorClaims"];

export type UnderwritingAnswersValues = Partial<
  UnitLevelUnderwritingAnswers & PolicyLevelUnderwritingAnswers
>;

export interface LoadingFrameState {
  show: boolean;
  messages: string[];
  maxLoadTime?: number;
  messageRotationInterval?: number;
}

interface ShowLoadingFrameActionPayload {
  messages: string[];
  maxLoadTime?: number;
  messageRotationInterval?: number;
}

interface PolicyState {
  loading: LoadingFrameState;
  loadingFromBackend: boolean;
  policy?: NewBusinessPolicyState;
  showInsuranceScoreNoHitModal: boolean;
  showMailingAddressReminder?: boolean;
  shouldPlanBeSelected: boolean;
  error?: Error;
}

const initialState: PolicyState = {
  // Loading spinner state
  loading: {
    show: false,
    messages: [],
  },
  // Whether any query/mutation is in progress
  loadingFromBackend: false,
  policy: undefined,
  shouldPlanBeSelected: false,
  showInsuranceScoreNoHitModal: false,
  showMailingAddressReminder: undefined,
  error: undefined,
};

const normalizeNames = (input: CreateOrUpdatePolicyholderInput) => ({
  ...omit(input, ["firstName", "lastName", "middleName"]),
  firstName: input.firstName?.toUpperCase(),
  lastName: input.lastName?.toUpperCase(),
  ...(input.type === PolicyholderType.Person
    ? { middleName: input.middleName?.toUpperCase() }
    : {}),
});

/**
 * Returns proper payload for mutation based on current form state and policy slice state
 * @param currentFormPayload            - Payload for mutation based on current form state
 * @param policyState                   - Current state of the policy in this slice
 */
const getCreateOrUpdateBasicPolicyDetailsWithHolderMutationPayload = (
  currentFormPayload: CreateOrUpdateBasicPolicyDetailsWithHolderMutationVariables,
  policyState?: NewBusinessPolicyState
): CreateOrUpdateBasicPolicyDetailsWithHolderMutationVariables | false => {
  if (!policyState) {
    return currentFormPayload;
  }

  const {
    additionalInsured,
    hasAnotherCoverTreePolicy,
    hasAutoPolicy,
    startDate,
    insuredDateOfBirth,
    policyLocator,
    physicallyMailDocuments,
    policyholder: {
      type,
      orgName,
      orgType,
      describeOrgType,
      repRelationToOrg,
      policyholderLocator,
      numberOfVehicles,
      occupationType,
      ...restPolicyHolderDetails
    },
    priorInsuranceDetails,
    units,
  } = policyState;

  const {
    address,
    construction,
    details: { communityName, purchaseDate, unitLocation },
    unitId,
    usage,
    vacancyReason,
  } = units[0];

  const {
    policyholderInput: policyHolderInputFromForm,
    policyInput: {
      additionalInsured: additionalInsuredFromForm,
      effectiveDate,
      hasAnotherCoverTreePolicy: hasAnotherCoverTreePolicyFromForm,
      hasAutoPolicy: hasAutoPolicyFromForm,
      insuredDateOfBirth: insuredDateOfBirthFromForm,
      physicallyMailDocuments: physicallyMailDocumentsFromForm,
      priorInsuranceDetails: priorInsuranceDetailsFromForm,
      units: unitsFromForm,
    },
  } = currentFormPayload;

  const {
    address: addressFromForm,
    construction: constructionFromForm,
    details: detailsFromForm,
    usage: usageFromForm,
  } = unitsFromForm![0];

  // If usage have changed - we need to create everything from scratch
  if (usage !== usageFromForm?.policyUsage) {
    return currentFormPayload;
  }

  // It is not allowed to change Policyholder address via this mutation
  const policyholderInput: CreateOrUpdatePolicyholderInput = {
    ...omitBy(
      pick(policyHolderInputFromForm, [
        "emailAddress",
        "firstName",
        "lastName",
        "secondaryContactNumber",
        "middleName",
        "primaryContactNumber",
      ]),
      isNil
    ),
    type: policyHolderInputFromForm.type,
    ...(policyHolderInputFromForm.type === PolicyholderType.Organization
      ? {
          orgName: policyHolderInputFromForm.orgName,
          orgType: policyHolderInputFromForm.orgType,
          ...(policyHolderInputFromForm.orgType === OrganizationType.NotListed
            ? { describeOrgType: policyHolderInputFromForm.describeOrgType }
            : {}),
          repRelationToOrg: policyHolderInputFromForm.repRelationToOrg,
        }
      : {
          ...(policyHolderInputFromForm.occupationType
            ? { occupationType: policyHolderInputFromForm.occupationType }
            : {}),
          ...(!isNil(policyHolderInputFromForm.numberOfVehicles)
            ? { numberOfVehicles: policyHolderInputFromForm.numberOfVehicles }
            : {}),
        }),
  };

  const policyHolderInputBasedOnPolicySlice: CreateOrUpdatePolicyholderInput = {
    ...omitBy(
      pick(restPolicyHolderDetails, [
        "emailAddress",
        "firstName",
        "lastName",
        "secondaryContactNumber",
        "middleName",
        "primaryContactNumber",
      ]),
      isNil
    ),
    type,
    ...(type === PolicyholderType.Organization
      ? {
          orgName,
          orgType,
          ...(orgType === OrganizationType.NotListed
            ? { describeOrgType }
            : {}),
          repRelationToOrg,
        }
      : {
          ...(occupationType ? { occupationType } : {}),
          ...(!isNil(numberOfVehicles)
            ? { numberOfVehicles: numberOfVehicles }
            : {}),
        }),
  };

  const unitDetailsBasedOnPolicySlice = {
    location: unitLocation,
    ...(communityName ? { communityName } : { communityName: undefined }),
    ...(purchaseDate ? { purchaseDate } : {}),
  };

  const isPolicyholderPayloadEqual = isEqual(
    normalizeNames(policyHolderInputBasedOnPolicySlice),
    normalizeNames(policyholderInput)
  );

  const isPriorInsuranceDetailsPayloadEqual =
    isEqual(
      omitBy(omit(priorInsuranceDetails, "__typename"), isNil),
      omitBy(priorInsuranceDetailsFromForm, isNil)
    ) ||
    // Exception for tenant flow and renting new home - in Socotra we save it as NoInsurance which breaks payload and policy state comparison
    (usage === PolicyUsage.Tenant &&
      priorInsuranceDetailsFromForm?.priorInsurance ===
        PriorInsurance.RentingNewHome &&
      priorInsuranceDetails.priorInsurance === PriorInsurance.NoInsurance);

  const isAdditionalInsuredEqual =
    (!additionalInsuredFromForm &&
      (!additionalInsured || additionalInsured.length === 0)) ||
    (additionalInsuredFromForm &&
      additionalInsured &&
      additionalInsured[0] &&
      additionalInsuredFromForm.firstName ===
        additionalInsured[0].adFirstName &&
      additionalInsuredFromForm.lastName === additionalInsured[0].adLastName &&
      additionalInsuredFromForm.relationship ===
        additionalInsured[0].relationshipIndividual);
  const isEffectiveDateEqual = !effectiveDate || startDate === effectiveDate;
  const isHasAnotherCoverTreePolicyEqual =
    hasAnotherCoverTreePolicy === hasAnotherCoverTreePolicyFromForm;
  const isHasAutoPolicyEqual = hasAutoPolicy === hasAutoPolicyFromForm;
  const isInsuredDateOfBirthEqual =
    insuredDateOfBirth === insuredDateOfBirthFromForm;
  const isPhysicallyMailDocumentsEqual =
    physicallyMailDocuments === physicallyMailDocumentsFromForm;

  const isUnitAddressEqual = isEqual(
    omitBy(omit(address, ["countryCode", "county", "__typename"]), isNil),
    addressFromForm
  );
  const isUnitConstructionEqual = isEqual(
    omitBy(omit(construction, ["roofCondition", "__typename"]), isNil),
    omitBy(constructionFromForm, isNil)
  );
  const isUnitDetailsEqual = isEqual(
    unitDetailsBasedOnPolicySlice,
    detailsFromForm
  );

  if (
    isPolicyholderPayloadEqual &&
    isPriorInsuranceDetailsPayloadEqual &&
    isAdditionalInsuredEqual &&
    isEffectiveDateEqual &&
    isHasAnotherCoverTreePolicyEqual &&
    isHasAutoPolicyEqual &&
    isInsuredDateOfBirthEqual &&
    isPhysicallyMailDocumentsEqual &&
    isUnitAddressEqual &&
    isUnitConstructionEqual &&
    isUnitDetailsEqual &&
    (usageFromForm.policyUsage !== PolicyUsage.Vacant ||
      usageFromForm.vacancyReason === vacancyReason)
  ) {
    // There's no need to do the request
    return false;
  }

  return {
    policyholderInput: {
      locator: policyholderLocator,
      ...(isPolicyholderPayloadEqual
        ? {}
        : {
            ...policyholderInput,
            leadSources: policyHolderInputFromForm.leadSources,
          }),
    },
    policyInput: {
      policyLocator,
      ...(additionalInsuredFromForm
        ? { additionalInsured: additionalInsuredFromForm }
        : {}),
      ...(isPriorInsuranceDetailsPayloadEqual
        ? {}
        : {
            priorInsuranceDetails: priorInsuranceDetailsFromForm,
          }),
      ...(isEffectiveDateEqual ? {} : { effectiveDate }),
      ...(isHasAnotherCoverTreePolicyEqual
        ? {}
        : { hasAnotherCoverTreePolicy: hasAnotherCoverTreePolicyFromForm }),
      ...(isHasAutoPolicyEqual ? {} : { hasAutoPolicy: hasAutoPolicyFromForm }),
      ...(isInsuredDateOfBirthEqual
        ? {}
        : { insuredDateOfBirth: insuredDateOfBirthFromForm }),
      ...(isPhysicallyMailDocumentsEqual
        ? {}
        : { physicallyMailDocuments: physicallyMailDocumentsFromForm }),
      // TODO: When there's multi unit, allow for deletion/addition of units
      units: [
        {
          unitId,
          ...(isUnitAddressEqual ? {} : { address: addressFromForm }),
          ...(isUnitConstructionEqual
            ? {}
            : { construction: constructionFromForm }),
          ...(isUnitDetailsEqual
            ? {}
            : {
                details: detailsFromForm,
              }),
          // Usage needs to be always sent
          usage: usageFromForm,
        },
      ],
    },
  };
};

export const policySlice = createAsyncThunkSlice({
  name: "policy",
  initialState,
  selectors: {
    selectPolicy: (state) => state.policy,
    selectPolicyLoading: (state) => state.loading,
    selectPolicyErrorState: (state) => state.error,
    selectPolicyBackendLoading: (state) => state.loadingFromBackend,
    selectPolicyStateFromAddress: (state) =>
      state.policy ? state.policy.units[0].address.state : "",
    selectPolicyholderPreviousAddress: (state) => state.policy?.previousAddress,
    selectShowInsuranceScoreNoHitModal: (state) =>
      state.showInsuranceScoreNoHitModal,
    selectShowMailingAddressReminder: (state) =>
      state.showMailingAddressReminder,
    selectShouldPlanBeSelected: (state) => state.shouldPlanBeSelected,
    // TODO: Potentially remove, after we enable useNewOffersAndSelectedQuotePricing feature flag
    selectCurrentOfferPricing: (state) =>
      state.policy?.offers.find(
        ({ quoteLocator }) =>
          quoteLocator === state.policy?.selectedQuoteLocator
      )?.pricing,
    selectCurrentlySelectedQuotePricing: (state) =>
      state.policy?.selectedQuotePricing,
    selectChosenExtraCoverages: (
      state
    ): [
      ExtraCoverageKeys,
      (
        | Omit<
            UnitLevelCoverages[Exclude<
              ExtraCoverageKeys,
              "scheduledPersonalProperty" | "identityFraud"
            >],
            "valueIsHidden"
          >
        | Omit<
            PolicyLevelCoverages[Exclude<
              keyof PolicyLevelCoverages,
              "__typename"
            >],
            "valueIsHidden"
          >
      ),
    ][] => {
      if (!state.policy) {
        return [];
      }

      const result: [
        ExtraCoverageKeys,
        (
          | Omit<
              UnitLevelCoverages[Exclude<
                ExtraCoverageKeys,
                "scheduledPersonalProperty" | "identityFraud"
              >],
              "valueIsHidden"
            >
          | Omit<
              PolicyLevelCoverages[Exclude<
                keyof PolicyLevelCoverages,
                "__typename"
              >],
              "valueIsHidden"
            >
        ),
      ][] = [];

      if (
        !state.policy.policyLevelCoverages.scheduledPersonalProperty
          .valueIsHidden
      ) {
        result.push([
          "scheduledPersonalProperty",
          {
            ...omit(
              state.policy.policyLevelCoverages.scheduledPersonalProperty,
              ["valueIsHidden", "value"]
            ),
            value:
              state.policy.policyLevelCoverages.scheduledPersonalProperty
                .value ?? [],
          },
        ]);
      }

      if (
        !state.policy.policyLevelCoverages.identityFraudExpense?.valueIsHidden
      ) {
        result.push([
          "identityFraud",
          omit(
            state.policy.policyLevelCoverages.identityFraudExpense,
            "valueIsHidden"
          ),
        ]);
      }

      result.push(
        ...Object.entries(
          omit(state.policy.units[0].unitLevelCoverages, "__typename")
          // TypeScript not really helping us here
          // We know that all entries, which are within extraCoverageKeys do have value and valueIsHidden property
        )
          .filter(
            ([key, coverage]) =>
              extraCoverageKeys.includes(key as ExtraCoverageKeys) &&
              !coverage["valueIsHidden" as keyof typeof coverage]
          )
          .map(
            ([key, extraCoverage]) =>
              [
                key,
                key === "secondaryResidenceLiability" ||
                key === "mineSubsidenceScheduled"
                  ? {
                      ...omit(extraCoverage, [
                        "valueIsHidden",
                        "value",
                        "__typename",
                      ]),
                      value:
                        extraCoverage["value" as keyof typeof extraCoverage] ??
                        [],
                    }
                  : {
                      ...omit(extraCoverage, "valueIsHidden"),
                      ...("value" in extraCoverage &&
                      !isNil(extraCoverage.value)
                        ? {
                            value: !!extraCoverage.value,
                            defaultValue:
                              extraCoverage.value ??
                              extraCoverage[
                                "defaultValue" as keyof typeof extraCoverage
                              ],
                          }
                        : {}),
                    },
              ] as [
                ExtraCoverageKeys,
                Omit<
                  UnitLevelCoverages[Exclude<
                    ExtraCoverageKeys,
                    "scheduledPersonalProperty" | "identityFraud"
                  >],
                  "valueIsHidden"
                >,
              ]
          )
      );

      // TODO: Change once we implement multi-unit
      return result;
    },
    selectUnderwritingQuestions: (state): UnderwritingQuestion[] =>
      state.policy
        ? [
            ...state.policy.policyLevelUnderwritingQuestions,
            ...state.policy.units
              .map((unit) => unit.unitLevelUnderwritingQuestions)
              .flat(),
          ]
        : [],
    selectUnderwritingAnswers: (state) =>
      mergeUnderwritingAnswers(
        state.policy?.policyLevelUnderwritingAnswers,
        (state.policy?.units ?? []).map(
          ({ unitLevelUnderwritingAnswers }) => unitLevelUnderwritingAnswers
        )
      ),
    selectPriorClaims: (state) => state.policy?.priorClaims || [],
    selectIsBookConversion: (state) =>
      !!state.policy?.bookConversion?.previousPolicyId,
    selectIsManualUWApproved: (state) => !!state.policy?.startDateAfterUWReview,
  },
  reducers: (c) => {
    const { create, createWithType } = typedReducerCreator(c);

    return {
      /**
       * Creates or updates policy with policyholder details
       */
      createOrUpdateBasicPolicyDetailsWithHolder: createWithType<
        NewBusinessPolicyState,
        {
          personalDetails?: PersonalDetailsForm;
          contactDetails?: ContactDetailsForm;
          extraQuestions?: ExtraQuestions;
        }
      >(
        async (
          {
            personalDetails: formPersonalDetails,
            contactDetails,
            extraQuestions: formExtraQuestions,
          },
          thunkAPI
        ) => {
          if (formPersonalDetails) {
            thunkAPI.dispatch(
              setPersonalDetails({
                personalDetails: formPersonalDetails,
              })
            );
          }

          if (formExtraQuestions) {
            thunkAPI.dispatch(setExtraQuestion(formExtraQuestions));
          }

          if (contactDetails) {
            thunkAPI.dispatch(
              setContactDetailsWithinPersonalDetails({
                personalDetails: contactDetails,
              })
            );
          }

          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const {
            effectiveDate,
            priorInsuranceDetails,
            usage: { homePurchaseTimeline, policyUsage, vacancyReason },
          } = getBasicPolicyDetails(newPolicyState);

          const leadSource = getLeadSource(newPolicyState);

          const { personalDetails } = getPersonalDetails(newPolicyState);

          const {
            describeOrgType,
            emailAddress,
            firstName,
            middleName,
            lastName,
            insuredDateOfBirth,
            orgName,
            orgType,
            primaryContactNumber,
            repRelationToOrg,
            secondaryContactNumber,
            occupationType,
            numberOfVehicles,
            type,
          } = personalDetails;

          if (!priorInsuranceDetails || !priorInsuranceDetails.priorInsurance) {
            throw new Error(
              "Prior insurance details are missing in application state"
            );
          }

          if (!policyUsage) {
            throw new Error("Policy usage is missing in application state");
          }

          const propertyAddress = getPropertyAddressDetails(newPolicyState);

          if (!propertyAddress) {
            throw new Error("House address is missing in application state");
          }

          const { city, mainAddressLine, lotUnit, postCode, state } =
            propertyAddress;

          const addressInput: GeoCodeInput = {
            city,
            ...(lotUnit ? { unitNumber: lotUnit } : {}),
            state,
            streetAddress: mainAddressLine,
            zipCode: postCode,
          };

          // TODO: Change behaviour while multi-unit policy feature is implemented
          const { construction, details } =
            getUnitConstructionAndDetails(newPolicyState)[0];

          const {
            additionalInsured,
            extraQuestions: {
              hasAnotherCoverTreePolicy,
              hasAutoPolicy,
              physicallyMailDocuments,
              noEmail,
            },
          } = getNonPolicyholderPersonalDetails(newPolicyState);

          const payloadBasedOnCurrentReduxFormsState: CreateOrUpdateBasicPolicyDetailsWithHolderMutationVariables =
            {
              policyholderInput: {
                emailAddress: noEmail
                  ? `${DEFAULT_NO_EMAIL_BASE}${primaryContactNumber}@covertree.com`
                  : emailAddress,
                firstName,
                ...(type === PolicyholderType.Person && middleName
                  ? { middleName }
                  : {}),
                lastName,
                primaryContactNumber,
                ...(secondaryContactNumber ? { secondaryContactNumber } : {}),
                type,
                ...(type === PolicyholderType.Organization
                  ? {
                      orgName,
                      orgType,
                      ...(orgType === OrganizationType.NotListed
                        ? { describeOrgType }
                        : {}),
                      repRelationToOrg,
                    }
                  : {
                      ...(occupationType ? { occupationType } : {}),
                      ...(!isNil(numberOfVehicles) ? { numberOfVehicles } : {}),
                    }),
                mailingAddress: addressInput,
                ...(leadSource ? { leadSources: [leadSource] } : {}),
              },
              policyInput: {
                ...(additionalInsured ? { additionalInsured } : {}),
                effectiveDate,
                hasAnotherCoverTreePolicy,
                hasAutoPolicy,
                insuredDateOfBirth,
                priorInsuranceDetails:
                  priorInsuranceDetails as PriorInsuranceDetailsInput,
                physicallyMailDocuments,
                units: [
                  {
                    address: addressInput,
                    construction,
                    details: {
                      ...details,
                      ...(homePurchaseTimeline
                        ? { purchaseDate: homePurchaseTimeline }
                        : {}),
                    },
                    usage: {
                      policyUsage,
                      ...(policyUsage === PolicyUsage.Rental
                        ? { shortTermRentalSurcharge: false }
                        : {}),
                      ...(policyUsage === PolicyUsage.Vacant
                        ? {
                            vacancyReason,
                          }
                        : {}),
                    },
                  },
                ],
              },
            };

          const variables =
            getCreateOrUpdateBasicPolicyDetailsWithHolderMutationPayload(
              payloadBasedOnCurrentReduxFormsState,
              newPolicyState.policy.policy
            );

          if (variables === false) {
            return newPolicyState.policy.policy;
          }

          thunkAPI.dispatch(
            showLoadingFrame({
              maxLoadTime: 30,
              messages: [
                "Collecting answers",
                "Analyzing and evaluating answers",
                "Simulating weather events",
                "Checking building codes",
                "Verifying roof condition",
                "Creating a personalized offer",
              ],
            })
          );

          try {
            const { data } = await thunkAPI.extra.client.mutate<
              CreateOrUpdateBasicPolicyDetailsWithHolderMutation,
              CreateOrUpdateBasicPolicyDetailsWithHolderMutationVariables
            >({
              mutation: CreateOrUpdateBasicPolicyDetailsWithHolderDocument,
              variables,
            });

            if (!data) {
              throw new Error("Failed to create policy");
            }

            const { createOrUpdateBasicPolicyDetailsWithHolder: policy } = data;

            // Cleanup cache from queries, which may return wrong data, because policy was updated
            thunkAPI.extra.client.cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getExtraCoveragePrices",
            });
            thunkAPI.extra.client.cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getQuoteDocuments",
            });
            thunkAPI.extra.client.cache.evict({
              id: "ROOT_QUERY",
              fieldName: "fetchUWDecisionAndGeneratePaymentPlans",
            });
            thunkAPI.extra.client.cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getPricedCustomizedPolicy",
            });

            return policy;
          } catch (err) {
            return thunkAPI.rejectWithValue(err);
          }
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            // Ensure to show modal once there's score hit & policyholder is different
            if (
              !payload.gotInsuranceScoreHit &&
              (!state.policy?.policyholder ||
                state.policy.policyholder.policyholderLocator !==
                  payload.policyholder.policyholderLocator ||
                state.policy.insuredDateOfBirth !== payload.insuredDateOfBirth)
            ) {
              state.showInsuranceScoreNoHitModal = true;
            }

            // Show mailing address modal if it's not shown yet or if the policyholder locator is different
            state.showMailingAddressReminder =
              state.showMailingAddressReminder === undefined ||
              state.policy?.units[0].usage !== payload.units[0].usage ||
              state.policy.policyholder.policyholderLocator !==
                payload.policyholder.policyholderLocator
                ? [
                    PolicyUsage.Seasonal,
                    PolicyUsage.Vacant,
                    PolicyUsage.Rental,
                  ].includes(payload.units[0].usage)
                : undefined;
            state.policy = payload;
          },
          rejected: (state, { payload, error }) => {
            const err = payload as Error;

            if (!err) {
              state.error = error as Error;
              return;
            }

            // Let's hope TypeScript will get better soon...
            if (isApolloError(err)) {
              const apolloError = err as ApolloError;
              if (apolloError.networkError) {
                state.error = apolloError.networkError;
                return;
              }

              if (apolloError.graphQLErrors.length > 0) {
                state.error = apolloError.graphQLErrors[0];
                return;
              }
              state.error = apolloError;
              return;
            }
            state.error = err;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Allows you to pick certain plan & loads extra coverages prices
       */
      chooseQuoteAndLoadExtraCoveragePrices: createWithType<
        SelectQuoteMutation["selectQuote"],
        { quoteLocator: string }
      >(
        async ({ quoteLocator }, thunkAPI) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const planChanged =
            newPolicyState.policy.policy!.selectedQuoteLocator !== quoteLocator;

          if (planChanged) {
            thunkAPI.extra.client.cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getExtraCoveragePrices",
            });
          }

          const { data } = await thunkAPI.extra.client.mutate<
            SelectQuoteMutation,
            SelectQuoteMutationVariables
          >({
            mutation: SelectQuoteDocument,
            variables: {
              locator: quoteLocator,
            },
          });

          if (!data) {
            throw new Error("Unable to select the Quote");
          }

          await thunkAPI.extra.client.query<
            GetExtraCoveragesPricesQuery,
            GetExtraCoveragesPricesQueryVariables
          >({
            query: GetExtraCoveragesPricesDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
            },
          });

          return data.selectQuote;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.shouldPlanBeSelected = true;

              state.policy.step = payload.step;
              state.policy.selectedQuoteLocator = payload.selectedQuoteLocator;
              state.policy.policyLevelUnderwritingAnswers =
                payload.policyLevelUnderwritingAnswers;

              state.policy.policyLevelCoverages = payload.policyLevelCoverages;

              state.policy.units = state.policy.units.map((unit) => {
                const unitFromPayload = payload.units.find(
                  ({ unitId }) => unitId === unit.unitId
                );
                if (unitFromPayload) {
                  return {
                    ...unit,
                    ...unitFromPayload,
                  };
                }

                return unit;
              });
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Saves picked Extra Coverages
       */
      saveExtraCoverages: createWithType<
        SaveExtraCoveragesMutation["saveExtraCoverages"],
        {
          policyLevelExtraCoverages: SavePolicyLevelExtraCoveragesInput;
          unitLevelExtraCoverages: SaveUnitLevelExtraCoveragesInput[];
        }
      >(
        async (
          { policyLevelExtraCoverages, unitLevelExtraCoverages },
          thunkAPI
        ) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            SaveExtraCoveragesMutation,
            SaveExtraCoveragesMutationVariables
          >({
            mutation: SaveExtraCoveragesDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
              policyLevelExtraCoverages,
              unitLevelExtraCoverages,
            },
          });

          if (!data?.saveExtraCoverages) {
            throw new Error("Error while saving extra coverages");
          }

          return data.saveExtraCoverages;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.shouldPlanBeSelected = true;

              state.policy.step = payload.step;
              state.policy.policyLevelCoverages = payload.policyLevelCoverages;

              state.policy.units = state.policy.units.map((unit) => {
                const unitFromPayload = payload.units.find(
                  ({ unitId }) => unitId === unit.unitId
                );
                if (unitFromPayload) {
                  return {
                    ...unit,
                    ...unitFromPayload,
                  };
                }

                return unit;
              });
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Saves policy and units level Underwriting Answers
       */
      saveUnderwritingAnswers: createWithType<
        UpdateUnderwritingAnswersMutation["updateUnderwritingAnswers"],
        {
          policyLevelUW: UnderwritingPolicyAnswersInput;
          unitLevelUW: UnderwritingUnitAnswersInput[];
        }
      >(
        async ({ policyLevelUW, unitLevelUW }, thunkAPI) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            UpdateUnderwritingAnswersMutation,
            UpdateUnderwritingAnswersMutationVariables
          >({
            mutation: UpdateUnderwritingAnswersDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
              policyLevelUW,
              unitLevelUW,
            },
          });

          if (!data?.updateUnderwritingAnswers) {
            throw new Error("Error while saving underwriting answers");
          }

          return data.updateUnderwritingAnswers;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.shouldPlanBeSelected = true;

              state.policy.step = payload.step;
              state.policy.policyLevelUnderwritingAnswers =
                payload.policyLevelUnderwritingAnswers;
              state.policy.units = state.policy.units.map((unit) => {
                const unitFromPayload = payload.units.find(
                  ({ unitId }) => unitId === unit.unitId
                );
                if (unitFromPayload) {
                  return {
                    ...unit,
                    ...unitFromPayload,
                  };
                }

                return unit;
              });
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Checks and adds Prior Claims
       */
      checkAndAddPriorClaim: createWithType<
        CheckAndAddPriorClaimMutation["checkAndAddPriorClaim"],
        { formerAddress: GeoCodeInput }
      >(
        async ({ formerAddress }, thunkAPI) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            CheckAndAddPriorClaimMutation,
            CheckAndAddPriorClaimMutationVariables
          >({
            mutation: CheckAndAddPriorClaimDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
              formerAddress,
            },
          });

          if (!data?.checkAndAddPriorClaim) {
            throw new Error("Error while checking prior claims");
          }

          return data.checkAndAddPriorClaim;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.shouldPlanBeSelected = true;

              state.policy.step = payload.step;
              state.policy.priorClaims = payload.priorClaims;
              state.policy.previousAddress = payload.previousAddress;
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Saves added Prior Claims
       */
      savePriorClaims: createWithType<
        SavePriorClaimsMutation["savePriorClaims"],
        {
          priorClaims: PriorClaimInput[];
        }
      >(
        async ({ priorClaims }, thunkAPI) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            SavePriorClaimsMutation,
            SavePriorClaimsMutationVariables
          >({
            mutation: SavePriorClaimsDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
              priorClaims,
            },
          });

          if (!data?.savePriorClaims) {
            throw new Error("Error while saving prior claims");
          }

          // Save extra coverages options to customization slice
          thunkAPI.dispatch(
            setCustomizationOptions({
              policyLevelCoverages: data.savePriorClaims.policyLevelCoverages,
              unitsWithCoverages: data.savePriorClaims.units.map((unit) =>
                pick(unit, ["exposureLocator", "unitLevelCoverages"])
              ),
            })
          );

          return data.savePriorClaims;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.shouldPlanBeSelected = true;

              state.policy.step = payload.step;
              state.policy.priorClaims = payload.priorClaims;
              // TODO: Remove offers update after we enable useNewOffersAndSelectedQuotePricing feature flag
              state.policy.offers = payload.offers;
              state.policy.selectedQuotePricing = payload.selectedQuotePricing;
              state.policy.internalTrackingSubcode =
                payload.internalTrackingSubcode;
              state.policy.policyholder = {
                ...state.policy.policyholder,
                ...payload.policyholder,
              };
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Updates Policy physicallyMailDocuments
       */
      updatePhysicallyMailDocuments: createWithType<
        UpdatePhysicallyMailDocumentsMutation["updatePhysicallyMailDocuments"],
        boolean
      >(
        async (physicallyMailDocuments, thunkAPI) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            UpdatePhysicallyMailDocumentsMutation,
            UpdatePhysicallyMailDocumentsMutationVariables
          >({
            mutation: UpdatePhysicallyMailDocumentsDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
              physicallyMailDocuments,
            },
          });

          if (!data?.updatePhysicallyMailDocuments) {
            throw new Error("Error while updating policy");
          }

          thunkAPI.dispatch(setExtraQuestion({ physicallyMailDocuments }));

          return data.updatePhysicallyMailDocuments;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.shouldPlanBeSelected = true;

              state.policy.physicallyMailDocuments =
                payload.physicallyMailDocuments;
              state.policy.step = payload.step;
              state.policy.priorClaims = payload.priorClaims;
              state.policy.offers = payload.offers;
              state.policy.policyholder.hasActivePolicies =
                payload.policyholder.hasActivePolicies;
              state.policy.selectedQuotePricing = payload.selectedQuotePricing;
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Updates Policyholder Details
       */
      updatePolicyholderDetails: createWithType<
        UpdatePolicyholderDetailsMutation["updatePolicyholderDetails"],
        Partial<UpdatePolicyholderDetailsInput> & { noEmail?: boolean }
      >(
        async ({ noEmail, ...policyholderDetails }, thunkAPI) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const { policy } = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            UpdatePolicyholderDetailsMutation,
            UpdatePolicyholderDetailsMutationVariables
          >({
            mutation: UpdatePolicyholderDetailsDocument,
            variables: {
              locator: policy.policy!.policyholder.policyholderLocator,
              input: policyholderDetails,
            },
          });

          if (!data?.updatePolicyholderDetails) {
            throw new Error("Error while updating policyholder details");
          }

          const { emailAddress, primaryContactNumber, secondaryContactNumber } =
            data.updatePolicyholderDetails;

          if (noEmail !== undefined) {
            thunkAPI.dispatch(
              setExtraQuestion({
                noEmail,
              })
            );
          }

          thunkAPI.dispatch(
            setPersonalDetails({
              personalDetails: {
                emailAddress: noEmail ? "" : emailAddress,
                primaryContactNumber,
                secondaryContactNumber,
              },
            })
          );

          return data.updatePolicyholderDetails;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.policy.policyholder = {
                ...state.policy.policyholder,
                ...payload,
              };
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Update customized policy
       */
      saveCustomizedPolicy: createWithType<
        SaveCustomizedPolicyMutation["saveCustomizedPolicy"],
        Omit<SaveCustomizedPolicyMutationVariables, "policyLocator">
      >(
        async (
          { effectiveDate, unitLevelCoverages, policyLevelCoverages },
          thunkAPI
        ) => {
          // Impossible to do it another way - we end up with circular dependency
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const { policy } = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            SaveCustomizedPolicyMutation,
            SaveCustomizedPolicyMutationVariables
          >({
            mutation: SaveCustomizedPolicyDocument,
            variables: {
              policyLocator: policy.policy!.policyLocator,
              effectiveDate,
              policyLevelCoverages,
              unitLevelCoverages,
            },
          });

          if (!data?.saveCustomizedPolicy) {
            throw new Error("Error while updating customized policy");
          }

          thunkAPI.dispatch(
            setPolicyEffectiveDate(data.saveCustomizedPolicy.startDate)
          );

          thunkAPI.dispatch(
            setCustomizationUnitsWithCoveragesCoverageBC({
              coverageB:
                data.saveCustomizedPolicy.units[0].unitLevelCoverages.coverageB,
              coverageC:
                data.saveCustomizedPolicy.units[0].unitLevelCoverages.coverageC,
            })
          );

          return data.saveCustomizedPolicy;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.policy.startDate = payload.startDate;
              state.policy.endDate = payload.endDate;

              const unitToEdit = state.policy.units.find(
                (unit) =>
                  unit.exposureLocator === payload.units[0].exposureLocator
              )!;

              unitToEdit.unitLevelCoverages =
                payload.units[0].unitLevelCoverages;
              unitToEdit.unitLevelUnderwritingAnswers =
                payload.units[0].unitLevelUnderwritingAnswers;
              unitToEdit.unitLevelUnderwritingQuestions =
                payload.units[0].unitLevelUnderwritingQuestions;
              state.policy.policyLevelCoverages = payload.policyLevelCoverages;
              state.policy.offers = payload.offers;
              state.policy.selectedQuotePricing = payload.selectedQuotePricing;
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      setIsAgentsSignResponsibility: create.reducer<boolean>(
        (state, { payload }) => {
          if (state.policy) {
            state.policy.isAgentsSignResponsibility = payload;
          }
        }
      ),
      /**
       * Hides loading frame
       */
      hideLoadingFrame: create.reducer((state) => {
        state.loading = {
          show: false,
          messages: [],
          maxLoadTime: undefined,
          messageRotationInterval: undefined,
        };
      }),
      /**
       * Shows loading frame with given messages
       */
      showLoadingFrame: create.reducer<ShowLoadingFrameActionPayload>(
        (state, { payload }) => {
          state.loading = {
            show: true,
            ...payload,
          };
        }
      ),
      /**
       * Sets showInsuranceScoreNoHitModal
       */
      setShowInsuranceScoreNoHitModal: create.reducer<boolean>(
        (state, { payload }) => {
          state.showInsuranceScoreNoHitModal = payload;
        }
      ),
      /**
       * Sets showMailingAddressReminder
       */
      setShowMailingAddressReminder: create.reducer<boolean>(
        (state, { payload }) => {
          state.showMailingAddressReminder = payload;
        }
      ),
      /**
       * Sets policy error state
       */
      setPolicyErrorState: create.reducer<Error>((state, { payload }) => {
        state.error = payload;
      }),
      /**
       * Resets Policy state
       */
      resetPolicy: create.reducer(() => initialState),
      /**
       * Continue quote with filling in the state
       */
      continueQuote: createWithType<
        | ContinueQuoteMutation["continueQuote"]
        | ConsumerContinueQuoteMutation["consumerContinueQuote"],
        ContinueQuoteMutationVariables | ConsumerContinueQuoteMutationVariables
      >(
        async (args, thunkAPI) => {
          try {
            const data = await (async () => {
              if ("payload" in args) {
                const { payload } = args;
                const { data } = await thunkAPI.extra.client.mutate<
                  ConsumerContinueQuoteMutation,
                  ConsumerContinueQuoteMutationVariables
                >({
                  mutation: ConsumerContinueQuoteDocument,
                  variables: { payload },
                });

                if (!data?.consumerContinueQuote) {
                  Toast.showError("Error while continuing quote");
                  throw new Error("Error while continuing quote");
                }

                return data.consumerContinueQuote;
              } else {
                const { policyLocator, effectiveDate } = args;
                const { data } = await thunkAPI.extra.client.mutate<
                  ContinueQuoteMutation,
                  ContinueQuoteMutationVariables
                >({
                  mutation: ContinueQuoteDocument,
                  variables: {
                    policyLocator,
                    effectiveDate,
                  },
                });

                if (!data?.continueQuote) {
                  Toast.showError("Error while continuing quote");
                  throw new Error("Error while continuing quote");
                }

                return data.continueQuote;
              }
            })();

            if ("canContinueQuote" in data) {
              return thunkAPI.rejectWithValue({
                message: "Continue quote is blocked",
                canContinueQuote: data.canContinueQuote,
              });
            }

            const { dynamicQuestions, policy } = data as Extract<
              typeof data,
              { policy: unknown }
            >;

            thunkAPI.dispatch(setDynamicQuestions(dynamicQuestions));

            thunkAPI.dispatch(
              setHomeAddressBasedOnPolicyDetails(pick(policy, "units"))
            );
            thunkAPI.dispatch(
              setBasicPolicyDetailsBasedOnPolicy(
                pick(policy, ["startDate", "priorInsuranceDetails", "units"])
              )
            );
            thunkAPI.dispatch(
              setUnitsConstructionAndDetailsBasedOnPolicy(pick(policy, "units"))
            );
            thunkAPI.dispatch(
              setPersonalDetailsBasedOnPolicy(
                pick(policy, [
                  "policyholder",
                  "insuredDateOfBirth",
                  "additionalInsured",
                  "hasAutoPolicy",
                  "hasAnotherCoverTreePolicy",
                  "physicallyMailDocuments",
                ])
              )
            );
            thunkAPI.dispatch(
              setCustomizationOptions({
                policyLevelCoverages: policy.policyLevelCoverages,
                unitsWithCoverages: policy.units.map((unit) => ({
                  exposureLocator: unit.exposureLocator,
                  unitLevelCoverages: unit.unitLevelCoverages,
                })),
              })
            );

            thunkAPI.extra.client.cache.writeQuery({
              query: GetDynamicQuestionsDocument,
              data: {
                getDynamicQuestions: dynamicQuestions,
              },
              variables: {
                effectiveDate: policy.startDate,
                policyUsage: policy.units[0].usage,
                state: policy.units[0].address.state,
              },
            });
            // FYI We don't need to execute canOperateInState, because continueQuote checks everything and throws error if needed
            thunkAPI.extra.client.cache.writeQuery({
              query: CanOperateInStateDocument,
              data: {
                canOperateInState: CanOperateInStateResult.Yes,
              },
              variables: {
                state: policy.units[0].address.state,
              },
            });

            return data;
          } catch (e) {
            // eslint-disable-next-line
            // @ts-ignore
            if (e?.graphQLErrors) {
              // eslint-disable-next-line
              // @ts-ignore
              const errors = e.graphQLErrors as {
                errorType: string;
                message: string;
              }[];

              const prioritizedError =
                errors.find(({ errorType }) =>
                  Object.values(RedZoneErrorType).includes(
                    errorType as RedZoneErrorType
                  )
                ) ??
                errors.find(({ errorType }) =>
                  Object.values(CountyBlockerErrorType).includes(
                    errorType as CountyBlockerErrorType
                  )
                ) ??
                errors.find(({ errorType }) =>
                  Object.values(PolicyErrors).includes(
                    errorType as PolicyErrors
                  )
                );

              if (prioritizedError) {
                return thunkAPI.rejectWithValue({
                  errorType: prioritizedError.errorType,
                  message: prioritizedError.message,
                });
              }
            }
            const message = e instanceof Error ? e.message : "unexpected error";
            return thunkAPI.rejectWithValue({ message });
          }
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            if (!("policy" in payload)) return;

            state.error = undefined;

            state.shouldPlanBeSelected = true;
            state.showInsuranceScoreNoHitModal =
              !payload.policy.gotInsuranceScoreHit;
            state.showMailingAddressReminder = [
              PolicyUsage.Seasonal,
              PolicyUsage.Vacant,
              PolicyUsage.Rental,
            ].includes(payload.policy.units[0].usage);

            state.policy = payload.policy;
          },
          rejected: (state, { payload, error }) => {
            const err = payload as Error;
            if (!err) {
              state.error = error as Error;
              return;
            }

            if (isApolloError(err)) {
              const apolloError = err as ApolloError;
              if (apolloError.networkError) {
                state.error = apolloError.networkError;
                return;
              }

              if (apolloError.graphQLErrors.length > 0) {
                state.error = apolloError.graphQLErrors[0];
                return;
              }
              state.error = apolloError;
              return;
            }
            state.error = err;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
      /**
       * Save internal tracking subcode
       */
      saveInternalTrackingSubcode: createWithType<
        SaveInternalTrackingSubcodeMutation["saveInternalTrackingSubcode"],
        { subcode: string }
      >(
        async ({ subcode }, thunkAPI) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const newPolicyState = thunkAPI.getState().newPolicy;

          const { data } = await thunkAPI.extra.client.mutate<
            SaveInternalTrackingSubcodeMutation,
            SaveInternalTrackingSubcodeMutationVariables
          >({
            mutation: SaveInternalTrackingSubcodeDocument,
            variables: {
              policyLocator: newPolicyState.policy.policy!.policyLocator,
              subcode,
            },
          });

          if (!data?.saveInternalTrackingSubcode) {
            throw new Error(
              "Could not save internal tracking subcode. Please try again."
            );
          }

          return data.saveInternalTrackingSubcode;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;

            if (state.policy) {
              state.policy.internalTrackingSubcode =
                payload.internalTrackingSubcode;
            } else {
              throw new Error("Policy is not defined");
            }
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
    };
  },
});

export const {
  actions: {
    createOrUpdateBasicPolicyDetailsWithHolder,
    chooseQuoteAndLoadExtraCoveragePrices,
    saveExtraCoverages,
    saveUnderwritingAnswers,
    checkAndAddPriorClaim,
    hideLoadingFrame,
    savePriorClaims,
    setShowInsuranceScoreNoHitModal,
    setShowMailingAddressReminder,
    setIsAgentsSignResponsibility,
    showLoadingFrame,
    resetPolicy,
    updatePhysicallyMailDocuments,
    updatePolicyholderDetails,
    saveCustomizedPolicy,
    setPolicyErrorState,
    continueQuote,
    saveInternalTrackingSubcode,
  },
  selectors: {
    selectPolicy,
    selectPolicyLoading,
    selectPolicyErrorState,
    selectPolicyBackendLoading,
    selectPolicyStateFromAddress,
    selectPolicyholderPreviousAddress,
    selectChosenExtraCoverages,
    selectShowInsuranceScoreNoHitModal,
    selectShowMailingAddressReminder,
    selectShouldPlanBeSelected,
    selectUnderwritingQuestions,
    selectUnderwritingAnswers,
    selectPriorClaims,
    selectCurrentOfferPricing,
    selectCurrentlySelectedQuotePricing,
    selectIsBookConversion,
    selectIsManualUWApproved,
  },
} = policySlice;
